// import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
// import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'

import { PIXEL_SIZE } from '../../core/chunks'
import { Location } from '../../core/Location'

import { Avatar } from '../extras/Avatar'
import { PhysicsLayers } from '../extras/PhysicsLayers'
import { Hands } from '../systems/InputSystem'
import { SweepHit } from '../systems/PhysicsSystem'
import { HitTypes } from '../systems/SpatialSystem'
import * as THREE from '../three'

import { Entity } from './Entity'
import { cloneDeep } from 'lodash-es'

// const lineMaterial = new LineMaterial({ color: 0x000000, transparent: true, opacity: 0.5, linewidth: 2 })

const FORWARD = new THREE.Vector3(0, 0, -1)
const BACKWARD = new THREE.Vector3(0, 0, 1)
const DOWN = new THREE.Vector3(0, -1, 0)
const UP = new THREE.Vector3(0, 1, 0)

const HAND_RADIUS = 0.05
const HAND_SLIP = 0.025

const ATTACK_VELOCITY = 2

const PACKET_RATE = 1 / 10

let tm

const v1 = new THREE.Vector3()
const v2 = new THREE.Vector3()
const v3 = new THREE.Vector3()
const v4 = new THREE.Vector3()
const v5 = new THREE.Vector3()
const q1 = new THREE.Quaternion()
const q2 = new THREE.Quaternion()
const e1 = new THREE.Euler(0, 0, 0, 'YXZ')
const e2 = new THREE.Euler(0, 0, 0, 'YXZ')
const iScale = new THREE.Vector3(1, 1, 1)
const o1 = new THREE.Object3D()
const o2 = new THREE.Object3D()

const _lPosition = new THREE.Vector3(0, 0, 0)
const _rPosition = new THREE.Vector3(0, 0, 0)
const _movement = new THREE.Vector3(0, 0, 0)

const h1 = new SweepHit()
const h2 = new SweepHit()

const spherecast_v1 = new THREE.Vector3()
const spherecast_v2 = new THREE.Vector3()
const spherecast_v3 = new THREE.Vector3()
const spherecast_v4 = new THREE.Vector3()
const spherecast_v5 = new THREE.Vector3()

const fistShape = new THREE.Mesh(new THREE.BoxGeometry(HAND_RADIUS * 2, HAND_RADIUS * 2, HAND_RADIUS * 2)) // box estimate

const loc1 = new Location()

// const touch = {
//   center: new THREE.Vector3(), // center of the sphere
//   point: new THREE.Vector3(), // surface touch point
//   normal: new THREE.Vector3(), // surface touch normal
// }

export class PlayerLocal extends Entity {
  constructor(client, data) {
    super(client, data)
    window.player = this

    this.avatar = new Avatar(this.client)

    // height from base to eyes
    // this.height = 8 * PIXEL_SIZE

    // sphere casting
    this.sphere = new PHYSX.PxSphereGeometry(0.1)
    this.sphereLayer = PhysicsLayers.environment.group

    // block overlap (when placing blocks no player bodys overlap)
    this.block = new PHYSX.PxBoxGeometry(0.5, 0.5, 0.5)
    this.blockLayer = PhysicsLayers.body.group

    // re-usable physx transform
    if (!tm) tm = new PHYSX.PxTransform(PHYSX.PxIDENTITYEnum.PxIdentity)

    this.leftHand = {
      gripPose: new THREE.Object3D(), // xr controller grip transform
      rayPose: new THREE.Object3D(), // xr controller ray target transform
      world: new THREE.Object3D(), // hand transform in world space
      velocity: createVelocityBuffer(4), // local velocity of hand movement
      pushback: new THREE.Vector3(), // movement pushed back onto the rig because the hand couldnt go where it wanted
      wasTouching: false, // hand was touching last update
      lastPosition: new THREE.Vector3(), // hand world position last update
      lastTouch: {
        // information on the last touch
        position: new THREE.Vector3(),
        normal: new THREE.Vector3(),
      },
      armed: false, // whether grip is being held down
      entityId: null, // an ID of an entity being held
      didAttack: false, // armed and did damage but hasnt moved "out" of the target yet
    }

    this.rightHand = {
      gripPose: new THREE.Object3D(), // xr controller grip transform
      rayPose: new THREE.Object3D(), // xr controller ray target transform
      world: new THREE.Object3D(), // hand transform in world space
      velocity: createVelocityBuffer(4), // local velocity of hand movement
      pushback: new THREE.Vector3(), // movement pushed back onto the rig because the hand couldnt go where it wanted
      wasTouching: false, // hand was touching last update
      lastPosition: new THREE.Vector3(), // hand position last update
      lastTouch: {
        // information on the last touch
        position: new THREE.Vector3(),
        normal: new THREE.Vector3(),
      },
      armed: false, // whether grip is being held down
      entityId: null, // an ID of an entity being held
      didAttack: false, // armed and did damage but hasnt moved "out" of the target yet
    }

    // player rig
    this.rig = new THREE.Object3D()
    this.rig.scale.setScalar(1.5)
    this.camera = this.client.graphics.camera
    this.camera.position.set(0, 1.7, 0)
    this.rig.add(this.camera)
    this.rig.add(this.leftHand.gripPose)
    this.rig.add(this.rightHand.gripPose)
    this.rig.add(this.leftHand.rayPose)
    this.rig.add(this.rightHand.rayPose)
    this.client.graphics.scene.add(this.rig)

    // capsule (body)
    {
      const radius = 0.1
      const height = 0.5
      const halfHeight = height / 2
      const capsuleGeo = new THREE.CapsuleGeometry(radius, height)
      capsuleGeo.translate(0, halfHeight + radius, 0)
      const capsuleMat = new THREE.MeshStandardMaterial({
        color: 'white',
        opacity: 0.5,
        transparent: true,
      })
      const capsuleMesh = new THREE.Mesh(capsuleGeo, capsuleMat)
      this.client.graphics.scene.add(capsuleMesh)
      const geometry = new PHYSX.PxCapsuleGeometry(radius, halfHeight)
      const material = this.client.physics.defaultMaterial
      const flags = new PHYSX.PxShapeFlags()
      flags.raise(PHYSX.PxShapeFlagEnum.eSCENE_QUERY_SHAPE | PHYSX.PxShapeFlagEnum.eSIMULATION_SHAPE)
      const shape = this.client.physics.physics.createShape(geometry, material, true, flags)
      // rotate to stand up
      const localPose = new PHYSX.PxTransform(PHYSX.PxIDENTITYEnum.PxIdentity)
      q1.set(0, 0, 0, 1).setFromAxisAngle(BACKWARD, Math.PI / 2)
      q1.toPxTransform(localPose)
      // move up so base is at 0,0,0
      v1.set(0, halfHeight + radius, 0)
      v1.toPxTransform(localPose)
      shape.setLocalPose(localPose)
      const layer = PhysicsLayers.body
      const pairFlags = PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_FOUND | PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_LOST | PHYSX.PxPairFlagEnum.eNOTIFY_CONTACT_POINTS | PHYSX.PxPairFlagEnum.eDETECT_CCD_CONTACT | PHYSX.PxPairFlagEnum.eSOLVE_CONTACT | PHYSX.PxPairFlagEnum.eDETECT_DISCRETE_CONTACT // prettier-ignore
      const filterData = new PHYSX.PxFilterData(layer.group, layer.mask, pairFlags, 0)
      shape.setQueryFilterData(filterData)
      shape.setSimulationFilterData(filterData)
      v1.copy(capsuleMesh.position).toPxTransform(tm)
      q1.copy(capsuleMesh.quaternion).toPxTransform(tm)
      const actor = this.client.physics.physics.createRigidDynamic(tm)
      actor.setMass(1)
      actor.setRigidBodyFlag(PHYSX.PxRigidBodyFlagEnum.eENABLE_CCD, true)
      actor.setRigidDynamicLockFlag(PHYSX.PxRigidDynamicLockFlagEnum.eLOCK_ANGULAR_X, true)
      actor.setRigidDynamicLockFlag(PHYSX.PxRigidDynamicLockFlagEnum.eLOCK_ANGULAR_Z, true)
      // actor.setActorFlag(PHYSX.PxActorFlagEnum.eDISABLE_GRAVITY, true)
      actor.attachShape(shape)
      this.capsuleHandle = this.client.physics.addActor(actor, {
        onInterpolate: position => {
          capsuleMesh.position.copy(position)
        },
      })
      this.capsuleActor = actor
      this.capsuleMesh = capsuleMesh
    }

    // left hand
    {
      // const lHandGeo = new THREE.SphereGeometry(HAND_RADIUS)
      // const lHandMat = new THREE.MeshStandardMaterial({ color: 'blue' })
      // this.lHand = new THREE.Mesh(lHandGeo, lHandMat)
      this.leftHand.gripPose.getWorldPosition(this.leftHand.world.position)
      // this.lHand.position.copy(this.leftHand.gripPose.position)
      // this.client.graphics.scene.add(this.lHand)
    }

    // right hand
    {
      // const rHandGeo = new THREE.SphereGeometry(HAND_RADIUS)
      // const rHandMat = new THREE.MeshStandardMaterial({ color: 'red' })
      // this.rHand = new THREE.Mesh(rHandGeo, rHandMat)
      this.rightHand.gripPose.getWorldPosition(this.rightHand.world.position)
      // this.rHand.position.copy(this.rightHand.gripPose.position)
      // this.client.graphics.scene.add(this.rHand)
    }

    // root and head tracking
    this.root = new THREE.Object3D()
    this.head = new THREE.Object3D()

    // const bodyGeo = new THREE.BoxGeometry(0.3, 0.6, 0.1)
    // const bodyMat = new THREE.MeshStandardMaterial({ color: 'yellow' })
    // this.bodyMesh = new THREE.Mesh(bodyGeo, bodyMat)
    // this.rig.add(this.bodyMesh)

    // hand last position tracking
    this.leftHand.gripPose.getWorldPosition(this.leftHand.lastPosition)
    this.rightHand.gripPose.getWorldPosition(this.rightHand.lastPosition)

    // rig velocity ring buffer
    this.velocityIdx = 0
    this.velocityBufferSize = 8
    this.velocityBuffer = []
    for (let i = 0; i < this.velocityBufferSize; i++) {
      this.velocityBuffer.push(new THREE.Vector3(0, 0, 0))
    }
    this.velocityAvg = new THREE.Vector3()

    // jump/velocity options
    this.velocityLimit = 0.4
    this.jumpMultiplier = 1.1 // 1.1
    this.jumpMaxSpeed = 6.5 // 6.5
    // debug mega jump?
    // this.velocityLimit = 0.05
    // this.jumpMultiplier = 10
    // this.jumpMaxSpeed = 999

    // rig delta tracking
    this.rigPositionLast = new THREE.Vector3()

    // distance at which you "unstick"
    this.unStickDistance = 1

    // camera delta tracking (local)
    this.camPositionLast = new THREE.Vector3()

    // head delta tracking (world space)
    this.headPositionLast = this.camera.getWorldPosition(new THREE.Vector3())

    this.client.setHot(this, true)
    this.client.on('xrSession', this.onXR)

    this.lastPacketTime = 0
    this.packet = {
      id: this.id,
      by: 0,
      hp: [0, 0, 0],
      hq: [0, 0, 0, 1],
      lp: [0, 0, 0],
      lq: [0, 0, 0, 1],
      rp: [0, 0, 0],
      rq: [0, 0, 0, 1],
    }

    this.setup(data)
  }

  async setup(data) {
    // this is called when entering an instance with new spawn position etc
    console.log('player setup', data)

    // update id and data
    this.id = data.id
    this.data.id = data.id
    this.data.owner = data.owner
    this.data.authority = data.authority
    this.data.position = data.position
    this.data.type = data.type
    this.data.name = data.name
    this.data.avatar = data.avatar
    this.data.visible = data.visible

    this.avatar.setup(data.avatar)

    // reset position to new spawn
    const pos = v1.fromArray(data.position)
    const capsule = this.capsuleMesh
    capsule.position.copy(pos)
    this.rig.position.copy(pos)
    this.rig.position.y = capsule.position.y - this.camera.position.y * this.rig.scale.y + this.avatar.height
    const pose = this.capsuleActor.getGlobalPose()
    pose.p.x = capsule.position.x
    pose.p.y = capsule.position.y
    pose.p.z = capsule.position.z
    this.capsuleHandle.snap(pose)
    // this.capsuleActor.setGlobalPose(pose)
    this.capsuleActor.setLinearVelocity(v2.set(0, 0, 0).toPxVec3())
    this.capsuleActor.setAngularVelocity(v2.set(0, 0, 0).toPxVec3())
    const camPos = this.camera.getWorldPosition(v2)
    this.leftHand.lastPosition.copy(camPos)
    this.rightHand.lastPosition.copy(camPos)
    this.leftHand.wasTouching = false
    this.rightHand.wasTouching = false
    this.velocityIdx = 0
    for (const vec3 of this.velocityBuffer) {
      vec3.set(0, 0, 0)
    }
    this.velocityAvg.set(0, 0, 0)
    this.rigPositionLast.copy(this.rig.position)
    this.camPositionLast.copy(this.camera.position)
    this.headPositionLast.copy(camPos)

    this.packet.id = data.id

    // disable gravity while we wait for chunk to build
    // because we want accurate frustum data sent to the worker for priority building
    this.capsuleActor.setActorFlag(PHYSX.PxActorFlagEnum.eDISABLE_GRAVITY, true)
    await this.client.chunks.waitForChunkAtPosition(...data.position)
    this.capsuleActor.setActorFlag(PHYSX.PxActorFlagEnum.eDISABLE_GRAVITY, false)

    // trigger initial xr status
    this.onXR(this.client.graphics.session)
  }

  onXR = session => {
    if (session) {
      this.xr = true
      if (!this.avatar.visible) {
        this.avatar.setVisible(true)
        this.data.visible = true
        this.client.network.send('entityModified', { id: this.id, visible: true })
      }
    } else {
      this.xr = false
      // if (this.avatar.visible) {
      //   this.avatar.setVisible(false)
      //   this.data.visible = false
      //   this.client.network.send('entityModified', { id: this.id, visible: false })
      // }
    }
  }

  update(delta) {
    const input = this.client.input
    const xr = this.xr

    const capsule = this.capsuleMesh

    // get headset movement XZ delta and apply to capsule so it follows
    const camPosition = v1.copy(this.camera.position).multiply(this.rig.scale)
    const camDelta = v2.copy(camPosition).sub(this.camPositionLast).divide(this.rig.scale)
    capsule.position.x += camDelta.x
    capsule.position.z += camDelta.z
    this.camPositionLast.copy(camPosition)

    // position rig XZ to follow natural physics of capsule (taking camera offset into account)
    this.rig.position.x = capsule.position.x - this.camera.position.x * this.rig.scale.x
    this.rig.position.z = capsule.position.z - this.camera.position.z * this.rig.scale.z

    // position rig Y to be locked at player height (head position)
    this.rig.position.y = capsule.position.y - this.camera.position.y * this.rig.scale.y + this.avatar.height
    // this.rig.position.y = capsule.position.y - (this.avatar.lastEyeY || 0)

    if (!xr) return

    // left hand
    this.leftHand.pushback.set(0, 0, 0)
    this.leftHand.gripPose.position.copy(input.lGripPose.position)
    this.leftHand.gripPose.quaternion.copy(input.lGripPose.quaternion)
    const lPosition = this.leftHand.gripPose.getWorldPosition(_lPosition)
    const lMovement = v1.copy(lPosition).sub(this.leftHand.lastPosition)
    lMovement.add(v2.copy(DOWN).multiplyScalar(2 * 9.8 * delta * delta)) // bias downward
    const lEnd = v2
    let lTouching = this.spherecast(this.leftHand.lastPosition, HAND_RADIUS, lMovement, lEnd, HAND_SLIP)
    if (lTouching) {
      if (this.leftHand.wasTouching) {
        this.leftHand.pushback.copy(this.leftHand.lastPosition).sub(lPosition)
      } else {
        this.leftHand.pushback.copy(lEnd).sub(lPosition)
      }
      this.leftHand.lastTouch.position.copy(lTouching.point)
      this.leftHand.lastTouch.normal.copy(lTouching.normal)
    }

    // right hand
    this.rightHand.pushback.set(0, 0, 0)
    this.rightHand.gripPose.position.copy(input.rGripPose.position)
    this.rightHand.gripPose.quaternion.copy(input.rGripPose.quaternion)
    const rPosition = this.rightHand.gripPose.getWorldPosition(_rPosition)
    const rMovement = v1.copy(rPosition).sub(this.rightHand.lastPosition)
    rMovement.add(v2.copy(DOWN).multiplyScalar(2 * 9.8 * delta * delta)) // bias downward
    const rEnd = v2
    let rTouching = this.spherecast(this.rightHand.lastPosition, HAND_RADIUS, rMovement, rEnd, HAND_SLIP)
    if (rTouching) {
      if (this.rightHand.wasTouching) {
        this.rightHand.pushback.copy(this.rightHand.lastPosition).sub(rPosition)
      } else {
        this.rightHand.pushback.copy(rEnd).sub(rPosition)
      }
      this.rightHand.lastTouch.position.copy(rTouching.point)
      this.rightHand.lastTouch.normal.copy(rTouching.normal)
    }

    // if touching, zero out capsule velocity
    if (lTouching || rTouching) {
      this.capsuleActor.setLinearVelocity(v1.set(0, 0, 0).toPxVec3())
      this.capsuleActor.setAngularVelocity(v1.set(0, 0, 0).toPxVec3())
    }

    const movement = _movement

    // apply hand movement
    if ((lTouching || this.leftHand.wasTouching) && (rTouching || this.rightHand.wasTouching)) {
      // this lets you grab stuff with both hands at the same time
      movement.copy(this.leftHand.pushback).add(this.rightHand.pushback).divideScalar(2)
    } else {
      movement.copy(this.leftHand.pushback).add(this.rightHand.pushback)
    }

    // check valid head movement
    const headPosition = this.camera.getWorldPosition(v1)
    const headMovement = v2.copy(headPosition).add(movement).sub(this.headPositionLast)
    const headEnd = v3
    let headTouching = this.spherecast(this.headPositionLast, 0.15, headMovement, headEnd, HAND_SLIP)
    if (headTouching) {
      movement.copy(headEnd).sub(this.headPositionLast)
    }

    if (movement.length() > 0.00001) {
      capsule.position.add(movement)
      this.rig.position.add(movement)
    }

    this.headPositionLast.copy(headPosition)

    // final left hand position
    const lPosition2 = this.leftHand.gripPose.getWorldPosition(_lPosition)
    const lMovement2 = v1.copy(lPosition2).sub(this.leftHand.lastPosition)
    const lEnd2 = v2
    const lFinalTouching = this.spherecast(this.leftHand.lastPosition, HAND_RADIUS, lMovement2, lEnd2, HAND_SLIP)
    if (lFinalTouching) {
      this.leftHand.lastPosition.copy(lEnd2)
      lTouching = true // double grab or head movement may cause this to now touch even if it wasnt
    } else {
      this.leftHand.lastPosition.copy(lPosition2)
    }

    // final right hand position
    const rPosition2 = this.rightHand.gripPose.getWorldPosition(_rPosition)
    const rMovement2 = v1.copy(rPosition2).sub(this.rightHand.lastPosition)
    const rEnd2 = v2
    const rFinalTouching = this.spherecast(this.rightHand.lastPosition, HAND_RADIUS, rMovement2, rEnd2, HAND_SLIP)
    if (rFinalTouching) {
      this.rightHand.lastPosition.copy(rEnd2)
      rTouching = true // double grab or head movement may cause this to now touch even if it wasnt
    } else {
      this.rightHand.lastPosition.copy(rPosition2)
    }

    // update velocity ring buffer
    this.velocityIdx = (this.velocityIdx + 1) % this.velocityBufferSize
    const oldestVelocity = this.velocityBuffer[this.velocityIdx]
    const currentVelocity = v1.copy(this.rig.position).sub(this.rigPositionLast).divideScalar(delta)
    this.velocityAvg.add(v2.copy(currentVelocity).sub(oldestVelocity).divideScalar(this.velocityBufferSize))
    this.velocityBuffer[this.velocityIdx].copy(currentVelocity)
    this.rigPositionLast.copy(this.rig.position)

    // apply velocity
    if (lTouching || rTouching) {
      const magnitude = this.velocityAvg.length()
      if (magnitude > this.velocityLimit) {
        this.capsuleActor.setActorFlag(PHYSX.PxActorFlagEnum.eDISABLE_GRAVITY, false)
        if (magnitude * this.jumpMultiplier > this.jumpMaxSpeed) {
          this.capsuleActor.setLinearVelocity(
            v1.copy(this.velocityAvg).normalize().multiplyScalar(this.jumpMaxSpeed).toPxVec3()
          )
        } else {
          this.capsuleActor.setLinearVelocity(v1.copy(this.velocityAvg).multiplyScalar(this.jumpMultiplier).toPxVec3())
        }
      }
    }

    // if (lTouching && this.leftHand.lastPosition.distanceTo(lPosition) > this.unStickDistance) {
    //   this.leftHand.lastPosition.copy(lPosition)
    //   lTouching = false
    // }

    // if (rTouching && this.rightHand.lastPosition.distanceTo(rPosition) > this.unStickDistance) {
    //   this.rightHand.lastPosition.copy(rPosition)
    //   rTouching = false
    // }

    // track hand local velocity (not world velocity)
    this.leftHand.velocity.push(this.leftHand.gripPose.position, delta)
    this.rightHand.velocity.push(this.rightHand.gripPose.position, delta)
    // world velocity if needed in future, could make this .worldVelocity
    // this.leftHand.velocity.push(lPosition, delta)
    // this.rightHand.velocity.push(rPosition, delta)

    // touch vibration + sounds
    if (lTouching && !this.leftHand.wasTouching) {
      // vibration
      input.vibrate(Hands.LEFT, 0.1, 50)
      // sound
      loc1.world
        .copy(this.leftHand.lastTouch.position)
        .add(v1.copy(this.leftHand.lastTouch.normal).multiplyScalar(-0.01))
      this.playTouchSound(loc1)
    }
    if (rTouching && !this.rightHand.wasTouching) {
      // vibration
      input.vibrate(Hands.RIGHT, 0.1, 50)
      // sound
      loc1.world
        .copy(this.rightHand.lastTouch.position)
        .add(v1.copy(this.rightHand.lastTouch.normal).multiplyScalar(-0.01))
      this.playTouchSound(loc1)
    }

    // track touching across frames
    this.leftHand.wasTouching = lTouching
    this.rightHand.wasTouching = rTouching

    // update hand visual
    this.leftHand.world.position.copy(this.leftHand.lastPosition)
    this.leftHand.gripPose.getWorldQuaternion(this.leftHand.world.quaternion)
    this.rightHand.world.position.copy(this.rightHand.lastPosition)
    this.rightHand.gripPose.getWorldQuaternion(this.rightHand.world.quaternion)

    const pose = this.capsuleActor.getGlobalPose()
    pose.p.x = capsule.position.x
    pose.p.y = capsule.position.y
    pose.p.z = capsule.position.z
    this.capsuleActor.setGlobalPose(pose)

    this.camera.getWorldPosition(this.head.position)
    this.camera.getWorldQuaternion(this.head.quaternion)

    // capsule.getWorldPosition(this.root.position)
    // capsule.getWorldQuaternion(this.root.quaternion)

    this.baseY = capsule.position.y

    this.avatar.update(delta, this.baseY, this.head, this.leftHand.world, this.rightHand.world)

    // todo: get block light level and update avatar.material.globalBrightness fraction
    // v1.copy(capsule.position)
    // v1.y += 0.5
    // loc1.world.copy(v1)
    // const chunk = this.client.chunks.get(loc1.chunkId)
    // const block =

    this.lastPacketTime += delta
    if (this.lastPacketTime > PACKET_RATE) {
      this.lastPacketTime -= PACKET_RATE
      this.packet.by = this.baseY
      this.head.position.toArray(this.packet.hp)
      this.head.quaternion.toArray(this.packet.hq)
      this.leftHand.world.position.toArray(this.packet.lp)
      this.leftHand.world.quaternion.toArray(this.packet.lq)
      this.rightHand.world.position.toArray(this.packet.rp)
      this.rightHand.world.quaternion.toArray(this.packet.rq)
      this.client.network.send('entityModified', this.packet)
    }

    this.updateHandGrip(this.leftHand, input.lGrip, Hands.LEFT)
    this.updateHandGrip(this.rightHand, input.rGrip, Hands.RIGHT)

    /**
     * TODO:
     * - spatial system needs new overlapSphere
     * - below code is wrong
     *   - if armed, run a spatial.overlapSphere to see if we hit anything, and if we do, damage it (fist or tool/weapon -> damages -> block or entity)
     *   - only on first hit, must move away and come back to hit again
     *   - blocks need damage tracker with visual, and only break when damage is full
     *     - damage tracker is singleton, if it changes block it resets to 0 (yes?)
     */

    if (this.leftHand.armed && this.leftHand.velocity.average.length() > ATTACK_VELOCITY) {
      let shape
      if (this.leftHand.entityId) {
        // todo: shape is the entity's hitbox/collider
      } else {
        shape = fistShape
      }
      shape.position.copy(this.leftHand.world.position)
      shape.updateMatrixWorld()
      const hit = this.client.spatial.shapecast(shape)
      if (!this.leftHand.didAttack && hit) {
        if (hit.type === HitTypes.BLOCK) {
          this.client.chunks.hitBlockAt(hit.location, 0.3)
          // this.client.chunks.breakBlockAt(hit.location)
        } else {
          // todo: damage entity?
        }
        this.leftHand.didAttack = true
      } else if (this.leftHand.didAttack && !hit) {
        this.leftHand.didAttack = false
      }
    }

    if (this.rightHand.armed && this.rightHand.velocity.average.length() > ATTACK_VELOCITY) {
      let shape
      if (this.rightHand.entityId) {
        // todo: shape is the entity's hitbox/collider
      } else {
        shape = fistShape
      }
      shape.position.copy(this.rightHand.world.position)
      shape.updateMatrixWorld()
      const hit = this.client.spatial.shapecast(shape)
      if (!this.rightHand.didAttack && hit) {
        if (hit.type === HitTypes.BLOCK) {
          this.client.chunks.hitBlockAt(hit.location, 0.3)
          // this.client.chunks.breakBlockAt(hit.location)
        } else {
          // todo: damage entity?
        }
        this.rightHand.didAttack = true
      } else if (this.rightHand.didAttack && !hit) {
        this.rightHand.didAttack = false
      }
    }

    // if (lTouching && this.leftHand.armed) {
    //   loc1.world
    //     .copy(this.leftHand.lastTouch.position)
    //     .add(v1.copy(this.leftHand.lastTouch.normal).multiplyScalar(-0.01))
    //   this.client.chunks.breakBlockAt(loc1)
    // }

    // if (rTouching && this.rightHand.armed) {
    //   loc1.world
    //     .copy(this.rightHand.lastTouch.position)
    //     .add(v1.copy(this.rightHand.lastTouch.normal).multiplyScalar(-0.01))
    //   this.client.chunks.breakBlockAt(loc1)
    // }

    // ============
    // OLD STUFF TO REMOVE:
    // ============

    // todo: solidify with inventory and stuff (for now uses a random block)

    // todo: remove all the stuff this was relying on, although the lTouchLast might be useful for physical tools like axes?
    // // remove block buttons
    // if (input.lBtn1.pressed) {
    //   // we cant rely on persistent contacts as simply touching things pushes us away
    //   // so we have to check if we're still close, eg we *just* touched something
    //   if (this.leftHand.lastPosition.distanceTo(this.leftHand.lastTouch.position) < HAND_RADIUS * 2) {
    //     // dig into the surface slightly to make sure we get the right block
    //     const pos = v1.copy(this.leftHand.lastTouch.position).add(v2.copy(this.leftHand.lastTouch.normal).negate().multiplyScalar(0.01))
    //     this.breakBlockAtPosition(pos)
    //   }
    // }
    // if (input.rBtn1.pressed) {
    //   // we cant rely on persistent contacts as simply touching things pushes us away
    //   // so we have to check if we're still close, eg we *just* touched something
    //   if (this.rightHand.lastPosition.distanceTo(this.rightHand.lastTouch.position) < HAND_RADIUS * 2) {
    //     // dig into the surface slightly to make sure we get the right block
    //     const pos = v1.copy(this.rightHand.lastTouch.position).add(v2.copy(this.rightHand.lastTouch.normal).negate().multiplyScalar(0.01))
    //     this.breakBlockAtPosition(pos)
    //   }
    // }

    // // cycle held items
    // if (input.lBtn2.pressed) {
    //   const items = this.client.inventory.items
    //   const curr = this.data.avatar.leftItem
    //   // if currently holding nothing, start at -1
    //   let idx = curr ? items.findIndex(i => i.id == curr.id) : -1
    //   // move to next item
    //   idx++
    //   let next = null
    //   if (idx < items.length) {
    //     next = items[idx]
    //   } else {
    //     // once we go past the end, wrap around to "nothing"
    //     next = null
    //     idx = -1
    //   }
    //   if (next !== curr) {
    //     this.equip('leftItem', cloneDeep(next))
    //   }
    // }
    // if (input.rBtn2.pressed) {
    //   const items = this.client.inventory.items
    //   const curr = this.data.avatar.rightItem
    //   // if currently holding nothing, start at -1
    //   let idx = curr ? items.findIndex(i => i.id == curr.id) : -1
    //   // move to next item
    //   idx++
    //   let next = null
    //   if (idx < items.length) {
    //     next = items[idx]
    //   } else {
    //     // once we go past the end, wrap around to "nothing"
    //     next = null
    //     idx = -1
    //   }
    //   if (next !== curr) {
    //     this.equip('rightItem', cloneDeep(next))
    //   }
    // }

    // // selection + place/remove
    // let hit

    // // left
    // this.leftHand.rayPose.position.copy(input.lTargetPose.position)
    // this.leftHand.rayPose.quaternion.copy(input.lTargetPose.quaternion)
    // this.leftHand.rayPose.getWorldPosition(v1)
    // this.leftHand.rayPose.getWorldQuaternion(q1)
    // v2.copy(FORWARD).applyQuaternion(q1)
    // hit = this.client.spatial.raycast(v1, v2, 100)
    // if (hit) {
    //   // beam (temp)
    //   if (!this.tempBeamL) {
    //     const geo = new THREE.BoxGeometry(0.003, 0.003, 10)
    //     geo.translate(0, 0, -5)
    //     const mat = new THREE.MeshStandardMaterial({ color: 'white', transparent: true, opacity: 0.5 })
    //     this.tempBeamL = new THREE.Mesh(geo, mat)
    //     this.client.graphics.scene.add(this.tempBeamL)
    //   }
    //   this.tempBeamL.position.copy(v1)
    //   this.tempBeamL.quaternion.copy(q1)
    //   this.tempBeamL.visible = true
    //   // hitbox
    //   const geo = hit.block.hitbox.lineGeometry
    //   if (!this.selectorL) {
    //     this.selectorL = new LineSegments2(geo, lineMaterial)
    //     this.client.graphics.scene.add(this.selectorL)
    //   }
    //   if (this.selectorL.geometry !== geo) {
    //     this.selectorL.geometry = geo
    //   }
    //   this.selectorL.position.set(hit.voxel.x, hit.voxel.y, hit.voxel.z)
    //   this.selectorL.visible = true
    //   // primary (trigger)
    //   if (input.lTrigger.pressed) {
    //     loc1.voxel.copy(hit.voxel)
    //     const item = this.data.avatar.leftItem
    //     // todo: if tool, use and allow use to optionally prevent default, eg not remove
    //     this.breakBlockAt(loc1)
    //   }
    //   // secondary (grip)
    //   if (input.lGrip.pressed) {
    //     loc1.voxel.copy(hit.voxel)
    //     const item = this.data.avatar.leftItem
    //     // todo: interact first and allow interaction to optionally prevent default, eg not place
    //     if (item?.type === 'block') {
    //       if (!hit.block.replaceable) {
    //         loc1.move(hit.normal)
    //       }
    //       this.placeBlockAt(loc1, item, true)
    //     }
    //   }
    // } else {
    //   if (this.selectorL) {
    //     this.selectorL.visible = false
    //   }
    //   if (this.tempBeamL) {
    //     this.tempBeamL.visible = false
    //   }
    // }

    // // right
    // this.rightHand.rayPose.position.copy(input.rTargetPose.position)
    // this.rightHand.rayPose.quaternion.copy(input.rTargetPose.quaternion)
    // this.rightHand.rayPose.getWorldPosition(v1)
    // this.rightHand.rayPose.getWorldQuaternion(q1)
    // v2.copy(FORWARD).applyQuaternion(q1)
    // hit = this.client.spatial.raycast(v1, v2, 100)
    // if (hit) {
    //   // beam (temp)
    //   if (!this.tempBeamR) {
    //     const geo = new THREE.BoxGeometry(0.003, 0.003, 10)
    //     geo.translate(0, 0, -5)
    //     const mat = new THREE.MeshStandardMaterial({ color: 'white', transparent: true, opacity: 0.5 })
    //     this.tempBeamR = new THREE.Mesh(geo, mat)
    //     this.client.graphics.scene.add(this.tempBeamR)
    //   }
    //   this.tempBeamR.position.copy(v1)
    //   this.tempBeamR.quaternion.copy(q1)
    //   this.tempBeamR.visible = true
    //   // hitbox
    //   const geo = hit.block.hitbox.lineGeometry
    //   if (!this.selectorR) {
    //     this.selectorR = new LineSegments2(geo, lineMaterial)
    //     this.client.graphics.scene.add(this.selectorR)
    //   }
    //   if (this.selectorR.geometry !== geo) {
    //     this.selectorR.geometry = geo
    //   }
    //   this.selectorR.position.set(hit.voxel.x, hit.voxel.y, hit.voxel.z)
    //   this.selectorR.visible = true
    //   // primary (trigger)
    //   if (input.rTrigger.pressed) {
    //     loc1.voxel.copy(hit.voxel)
    //     const item = this.data.avatar.rightItem
    //     // todo: if tool, use and allow use to optionally prevent default, eg not remove
    //     this.breakBlockAt(loc1)
    //   }
    //   // secondary (grip)
    //   if (input.rGrip.pressed) {
    //     loc1.voxel.copy(hit.voxel)
    //     const item = this.data.avatar.rightItem
    //     // todo: interact first and allow interaction to optionally prevent default, eg not place
    //     if (item?.type === 'block') {
    //       if (!hit.block.replaceable) {
    //         loc1.move(hit.normal)
    //       }
    //       this.placeBlockAt(loc1, item, true)
    //     }
    //   }
    // } else {
    //   if (this.selectorR) {
    //     this.selectorR.visible = false
    //   }
    //   if (this.tempBeamR) {
    //     this.tempBeamR.visible = false
    //   }
    // }
  }

  updateHandGrip(hand, grip) {
    // released - drop everything
    if (grip.released) {
      if (hand.entityId) {
        this.dropEntity(hand)
      }
      hand.armed = false
      return
    }
    // pressed - try grab or activate fists
    if (grip.pressed) {
      const nearby = this.findGrabbableNear(hand.world)
      if (nearby) {
        this.grabEntity(hand, nearby)
      }
      hand.armed = true
    }
    // while held - update armed state
    if (grip.down) {
      // const item = hand.heldEntity?.asset
      // hand.armed = hand.fist || item?.grip?.mode === 'melee' // TODO: sudo code
    }
  }

  findGrabbableNear(pos) {
    // TODO: this needs to be a hitbox shapecast check, not physics!
    const origin = v1.copy(pos) // .toPxVec3 required on v1
    const hits = this.client.physics.overlapSphere(0.15, origin, PhysicsLayers.item.group)
    if (!hits.length) return null
    // find closest (TODO: sudo code below)
    let closest = null
    let closestDist = Infinity
    for (const hit of hits) {
      const entity = this.client.entities.getByActor(hit.actor)
      if (!entity) continue
      const dist = entity.position.distanceTo(origin)
      if (dist < closestDist) {
        closest = entity
        closestDist = dist
      }
    }
    return closest
  }

  grabEntity(hand, entity) {
    // hand.heldEntity = entity
    // entity.setParent(hand.gripPose)
    // entity.mesh.position.set(0, 0, 0) // or offset based on grip point
    // entity.disablePhysics()
    // // Tell server
    // this.client.network.send('grab', {
    //   hand: hand === this.leftHand ? 'left' : 'right',
    //   entityId: entity.id,
    // })
  }

  dropEntity(hand) {
    // const entity = hand.heldEntity
    // if (!entity) return
    // entity.setParent(null)
    // entity.mesh.position.copy(hand.world.position)
    // entity.enablePhysics()
    // entity.setVelocity(this.velocityAvg) // toss it
    // hand.heldEntity = null
    // // Tell server
    // this.client.network.send('drop', {
    //   hand: hand === this.leftHand ? 'left' : 'right',
    // })
  }

  playTouchSound(loc) {
    const block = this.client.chunks.getBlockAtLocation(loc)
    if (!block?.asset.touchSound) return
    const sound = this.client.registry.get('sound', block.asset.touchSound)
    console.log('foo', sound)
    const opts = this.client.registry.prepareSound(sound, loc.world)
    console.log('foo2', opts)
    if (!opts) return
    this.client.audio.sounds.play(opts)
  }

  // placeBlockAt(loc, item, replace) {
  //   return
  //   // cant place a block if our capsule is in the volume
  //   const blocked = !!this.client.physics.overlap(this.block, loc.world, this.blockLayer)
  //   if (blocked) return
  //   // get chunk
  //   const chunk = this.client.chunks.get(loc.chunkId)
  //   // cant place a block outside chunks
  //   if (!chunk) return console.log('placeBlockAt: no chunk at location')
  //   // cant place a block if a block is already there
  //   if (!replace) {
  //     const currBlock = chunk.getBlockAtIdx(loc.voxelIdx)
  //     if (currBlock.id !== 'air') return
  //   }
  //   // ensure block is registered
  //   this.client.chunks.ensureItem(item)
  //   // place the block
  //   const block = this.client.chunks.blocks.resolve(item.id)
  //   chunk.setStateAtIdx(loc.voxelIdx, block.state)
  //   // send to server
  //   this.client.network.send('blockPlace', {
  //     voxel: loc.voxel.toArray(),
  //     itemId: item.id,
  //   })
  // }

  // breakBlockAt(loc) {
  //   return
  //   // get chunk
  //   const chunk = this.client.chunks.get(loc.chunkId)
  //   // cant remove a block outside a chunk
  //   if (!chunk) return console.log('breakBlockAt: no chunk at location')
  //   // cant remove a block if its already air
  //   const currBlock = chunk.getBlockAtIdx(loc.voxelIdx)
  //   if (currBlock.id === 'air') return
  //   // modify it!
  //   const block = this.client.chunks.blocks.resolve('air')
  //   chunk.setStateAtIdx(loc.voxelIdx, block.state)
  //   // send to server
  //   this.client.network.send('blockBreak', {
  //     voxel: loc.voxel.toArray(),
  //   })
  // }

  sweep(origin, radius, direction, maxDistance, hit) {
    this.sphere.radius = radius
    return this.client.physics.sweep(this.sphere, origin, direction, maxDistance, this.sphereLayer, hit)
  }

  overlap(origin, radius) {
    this.sphere.radius = radius
    return this.client.physics.overlap(this.sphere, origin, this.sphereLayer)
  }

  spherecast(start, radius, movement, end, slip = 0) {
    const skin = radius * (1 - 0.995)
    // overlap test
    const overlap = this.overlap(start, radius - skin)
    if (overlap) {
      end.copy(start)
      return null
    }
    const dir = spherecast_v1.copy(movement).normalize()
    const maxDistance = spherecast_v2.copy(movement).length() + skin
    const hit = this.sweep(start, radius - skin, dir, maxDistance, h1)
    // no hit? full movement
    if (!hit) {
      end.copy(start).add(movement)
      return null
    }
    // no slip? move to surface
    if (!slip) {
      end.copy(start).add(spherecast_v2.copy(dir).multiplyScalar(hit.distance - skin))
      return hit
    }
    // otherwise calculate slip
    const surface = spherecast_v3.copy(start).add(dir.multiplyScalar(hit.distance - skin))
    // project intended movement onto surface plane
    // this removes the component going into the wall, keeping only the slipping part
    const dotProduct = movement.dot(hit.normal)
    // if moving away surface (dot > 0), no slip needed
    if (dotProduct >= 0) {
      end.copy(surface)
      return hit
    }
    // remove perpendicular component to get the slip direction
    const slipDir = spherecast_v4.copy(movement).sub(spherecast_v5.copy(hit.normal).multiplyScalar(dotProduct))
    // slip amount is naturally proportional to the angle:
    // - if hitting head-on (perpendicular), slipDir is near zero
    // - if hitting at a glancing angle, slipDir preserves most of the movement
    // scale the slipping based on how much we couldn't complete the original movement
    const remainingDistance = maxDistance - hit.distance
    const slipScale = Math.min(remainingDistance / maxDistance, 1.0)
    // sweep toward slip
    const hit2 = this.sweep(surface, radius - skin, slipDir, slip * slipScale, h2)
    if (hit2) {
      end.copy(surface).add(spherecast_v2.copy(slipDir).multiplyScalar(hit2.distance - skin))
      return hit2
    }
    // apply slip
    end.copy(surface).add(slipDir.multiplyScalar(slip * slipScale - skin))
    return hit
    // // anti-clipping through geometry check (note: testing not working properly)
    // const dir2 = spherecast_v1.copy(surface).sub(start).normalize()
    // const hit2 = this.sweep(start, radius - skin - skin, dir2, start.distanceTo(surface) + skin + skin, h2)
    // if (hit2) {
    //   end.copy(start)
    //   return false
    // } else {
    //   return true
    // }
  }

  getVoiceTransform(pos, qua) {
    pos.copy(this.head.position)
    qua.copy(this.head.quaternion)
  }

  setVoiceActive(active) {
    // ...
  }

  equip(slot, item, emit = true) {
    this.data.avatar[slot] = item
    this.avatar.setup(this.data.avatar)
    if (emit) {
      this.client.network.send('equip', [this.id, slot, item?.id || null])
    }
  }
}

function createVelocityBuffer(size) {
  const buffer = []
  const average = new THREE.Vector3()
  let idx = 0
  let lastPos = new THREE.Vector3()
  let hasLastPos = false

  // Pre-allocate
  for (let i = 0; i < size; i++) {
    buffer.push(new THREE.Vector3())
  }

  return {
    average,
    push(position, delta) {
      if (hasLastPos) {
        buffer[idx].copy(position).sub(lastPos).divideScalar(delta)
        idx = (idx + 1) % size
      }

      lastPos.copy(position)
      hasLastPos = true

      average.set(0, 0, 0)
      for (const v of buffer) {
        average.add(v)
      }
      average.divideScalar(size)
    },
    reset(position) {
      for (const v of buffer) {
        v.set(0, 0, 0)
      }
      average.set(0, 0, 0)
      idx = 0
      if (position) {
        lastPos.copy(position)
        hasLastPos = true
      } else {
        hasLastPos = false
      }
    },
  }
}
